<template>
  <div class="resumen-admin">
    <div v-if="cargando" class="text-center py-4">
      <div class="spinner-border spinner-border-sm text-primary me-2"></div>
      <span class="text-muted small">Cargando evolución, FIRE y salud…</span>
    </div>

    <template v-else>
      <!-- Evolución Financiera -->
      <section class="mb-4">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 class="mb-0">
              <i class="bi bi-graph-up-arrow me-2 text-primary"></i>Evolución Financiera
            </h4>
            <small v-if="hoy" class="text-muted">Datos de hoy — {{ hoy[1] }}</small>
          </div>
          <router-link to="/evolucion" class="btn btn-sm btn-outline-primary">
            Ver detalle <i class="bi bi-arrow-right ms-1"></i>
          </router-link>
        </div>

        <div v-if="errorEvolucion" class="alert alert-warning small py-2 mb-3">
          <i class="bi bi-exclamation-triangle me-1"></i>{{ errorEvolucion }}
        </div>

        <div v-else-if="hoy" class="row g-3">
          <div class="col-6 col-md-3">
            <div
              class="card shadow-sm h-100 resumen-kpi evo-importe-total-card"
              :class="{ 'evo-importe-record': esNuevoMaximoHistorico }"
              :style="esNuevoMaximoHistorico
                ? 'border-left:4px solid #ffc107; background:#fffbeb;'
                : 'border-left:4px solid #0d6efd;'"
            >
              <div class="card-body py-3">
                <div class="resumen-kpi-label">
                  <i class="bi bi-wallet2 me-1"></i>Importe Total
                  <span v-if="esNuevoMaximoHistorico" class="badge bg-warning text-dark ms-1">
                    <i class="bi bi-trophy-fill me-1"></i>Máx. histórico
                  </span>
                </div>
                <div class="d-flex align-items-baseline flex-wrap gap-2">
                  <div
                    class="resumen-kpi-value evo-importe-total-valor"
                    :class="{ 'evo-importe-record__valor': esNuevoMaximoHistorico }"
                  >{{ hoy[2] }}</div>
                  <span
                    v-if="pctVsMaxLabel"
                    class="evo-pct-vs-max"
                    :class="pctVsMaximo >= 0 ? 'evo-pct-vs-max--up' : 'evo-pct-vs-max--down'"
                    :title="maxHistorico ? ('vs máx. histórico ' + maxHistorico[2]) : ''"
                  >{{ pctVsMaxLabel }}</span>
                </div>
                <div class="resumen-kpi-sub">Sin cripto: {{ hoy[3] }}</div>
                <div v-if="maxHistorico" class="resumen-kpi-sub mt-1">
                  {{ esNuevoMaximoHistorico ? 'Anterior' : 'Máx. histórico' }}:
                  {{ maxHistorico[2] }}
                  <span v-if="maxHistorico[1]">({{ maxHistorico[1] }})</span>
                </div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card shadow-sm h-100 resumen-kpi" style="border-left:4px solid #198754;">
              <div class="card-body py-3">
                <div class="resumen-kpi-label"><i class="bi bi-graph-up me-1"></i>Beneficio Global</div>
                <div class="resumen-kpi-value">{{ hoy[6] }}</div>
                <div class="resumen-kpi-sub">Rentabilidad: {{ hoy[8] }}</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card shadow-sm h-100 resumen-kpi" style="border-left:4px solid #dc3545;">
              <div class="card-body py-3">
                <div class="resumen-kpi-label"><i class="bi bi-fire me-1"></i>% FIRE Generado</div>
                <div class="resumen-kpi-value" :class="claseFirePct(hoy[12])">{{ hoy[12] }}</div>
                <div class="resumen-kpi-sub">Retiro: {{ hoy[13] }}</div>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card shadow-sm h-100 resumen-kpi" style="border-left:4px solid #fd7e14;">
              <div class="card-body py-3">
                <div class="resumen-kpi-label"><i class="bi bi-currency-bitcoin me-1"></i>Cripto</div>
                <div class="resumen-kpi-value">{{ hoy[5] }}</div>
                <div class="resumen-kpi-sub">Propio: {{ hoy[4] }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Salud -->
      <section class="mb-2">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 class="mb-0">
              <i class="bi bi-heart-pulse me-2 text-danger"></i>Salud
            </h4>
            <small class="text-muted">Nutrición, Oura, FitDays, Coros y TrainingPeaks</small>
          </div>
          <router-link to="/salud" class="btn btn-sm btn-outline-secondary">
            Ver todo <i class="bi bi-arrow-right ms-1"></i>
          </router-link>
        </div>

        <div class="row g-3">
          <!-- Nutrición -->
          <div class="col-6 col-lg-3">
            <router-link
              to="/nutricion"
              class="card shadow-sm h-100 resumen-salud-card resumen-salud-card--link text-decoration-none text-reset"
              aria-label="Ver detalle de Nutrición"
            >
              <div class="card-body py-3 d-flex flex-column">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <div class="resumen-salud-icon resumen-salud-icon--nutricion">
                    <i class="bi bi-egg-fried"></i>
                  </div>
                  <span class="fw-semibold small">Nutrición</span>
                </div>
                <template v-if="menuHoy">
                  <div class="small fw-semibold text-truncate">Hoy: {{ menuHoy.comida || '—' }}</div>
                  <div class="text-muted" style="font-size:0.75rem;">
                    Cena: {{ menuHoy.cena || '—' }}
                  </div>
                  <div v-if="menuDiasCount" class="text-muted" style="font-size:0.75rem;">
                    {{ menuDiasCount }} días en el menú
                  </div>
                </template>
                <p v-else class="text-muted small mb-0">Sin menú de próximos días</p>
                <span class="btn btn-sm btn-outline-secondary mt-auto align-self-start resumen-salud-cta">
                  Ver detalle <i class="bi bi-arrow-right ms-1"></i>
                </span>
              </div>
            </router-link>
          </div>

          <!-- Oura -->
          <div class="col-6 col-lg-3">
            <router-link
              to="/salud-oura"
              class="card shadow-sm h-100 resumen-salud-card resumen-salud-card--link text-decoration-none text-reset"
              aria-label="Ver detalle de Oura"
            >
              <div class="card-body py-3 d-flex flex-column">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <div class="resumen-salud-icon resumen-salud-icon--oura">
                    <i class="bi bi-activity"></i>
                  </div>
                  <span class="fw-semibold small">Oura</span>
                </div>
                <template v-if="ouraStatus?.connected && ouraLatest">
                  <div class="d-flex justify-content-between small">
                    <span>Readiness</span>
                    <strong>{{ fmtScore(ouraLatest.readiness) }}</strong>
                  </div>
                  <div class="d-flex justify-content-between small">
                    <span>Sleep</span>
                    <strong>{{ fmtScore(ouraLatest.sleep) }}</strong>
                  </div>
                  <div class="d-flex justify-content-between small">
                    <span>Activity</span>
                    <strong>{{ fmtScore(ouraLatest.activity) }}</strong>
                  </div>
                </template>
                <p v-else class="text-muted small mb-0">
                  {{ ouraStatus?.connected ? 'Sin datos recientes' : 'Sin conectar' }}
                </p>
                <div
                  v-if="ouraStatus?.connected"
                  class="text-muted resumen-salud-sync"
                  :title="ouraLastSyncTitle"
                >
                  <i class="bi bi-arrow-repeat me-1"></i>{{ ouraLastSyncLabel }}
                </div>
                <span class="btn btn-sm btn-outline-secondary mt-auto align-self-start resumen-salud-cta">
                  Ver detalle <i class="bi bi-arrow-right ms-1"></i>
                </span>
              </div>
            </router-link>
          </div>

          <!-- FitDays -->
          <div class="col-6 col-lg-3">
            <router-link
              to="/salud-fitdays"
              class="card shadow-sm h-100 resumen-salud-card resumen-salud-card--link text-decoration-none text-reset"
              aria-label="Ver detalle de FitDays"
            >
              <div class="card-body py-3 d-flex flex-column">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <div class="resumen-salud-icon resumen-salud-icon--fitdays">
                    <i class="bi bi-speedometer2"></i>
                  </div>
                  <span class="fw-semibold small">FitDays</span>
                </div>
                <template v-if="fitdaysStatus?.configured && fitdaysLatest">
                  <div class="resumen-peso-value">{{ fmtPeso(fitdaysLatest.peso_kg) }}</div>
                  <div class="text-muted small">kg</div>
                  <div v-if="fitdaysLatest.imc != null" class="text-muted" style="font-size:0.75rem;">
                    IMC {{ fmtDecimal(fitdaysLatest.imc) }}
                  </div>
                </template>
                <p v-else class="text-muted small mb-0">
                  {{ fitdaysStatus?.configured ? 'Sin datos recientes' : 'Sin configurar' }}
                </p>
                <div
                  v-if="fitdaysStatus?.configured"
                  class="text-muted resumen-salud-sync"
                  :title="fitdaysMedicionTitle"
                >
                  <i class="bi bi-calendar3 me-1"></i>{{ fitdaysMedicionLabel }}
                </div>
                <span class="btn btn-sm btn-outline-secondary mt-auto align-self-start resumen-salud-cta">
                  Ver detalle <i class="bi bi-arrow-right ms-1"></i>
                </span>
              </div>
            </router-link>
          </div>

          <!-- Coros -->
          <div class="col-6 col-lg-3">
            <router-link
              to="/salud-coros"
              class="card shadow-sm h-100 resumen-salud-card resumen-salud-card--link text-decoration-none text-reset"
              aria-label="Ver detalle de Coros"
            >
              <div class="card-body py-3 d-flex flex-column">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <div class="resumen-salud-icon resumen-salud-icon--coros">
                    <i class="bi bi-smartwatch"></i>
                  </div>
                  <span class="fw-semibold small">Coros</span>
                </div>
                <template v-if="corosStatus?.configured && (corosTraining?.status || proximoEntreno)">
                  <div v-if="corosTraining?.status" class="small fw-semibold" style="color:#ff7a2f;">
                    {{ corosTraining.status.label }}
                    <span v-if="corosTraining.recoveryPct != null" class="text-muted fw-normal">
                      · Recup. {{ Math.round(corosTraining.recoveryPct) }}%
                    </span>
                  </div>
                  <div v-if="proximoEntreno" class="small fw-semibold text-truncate">{{ proximoEntreno.name || 'Entreno' }}</div>
                  <div class="text-muted" style="font-size:0.75rem;">
                    <template v-if="proximoEntreno">
                      {{ fmtEntrenoDia(proximoEntreno) }}
                      <span v-if="proximoEntreno.sport_name"> · {{ proximoEntreno.sport_name }}</span>
                    </template>
                    <template v-else-if="corosTraining?.runningStatus != null">
                      Carrera {{ Number(corosTraining.runningStatus).toFixed(1) }}
                    </template>
                  </div>
                </template>
                <p v-else class="text-muted small mb-0">
                  {{ corosStatus?.configured ? 'Sin entrenos planificados' : 'Sin configurar' }}
                </p>
                <span class="btn btn-sm btn-outline-secondary mt-auto align-self-start resumen-salud-cta">
                  Ver detalle <i class="bi bi-arrow-right ms-1"></i>
                </span>
              </div>
            </router-link>
          </div>

          <!-- TrainingPeaks -->
          <div class="col-6 col-lg-3">
            <router-link
              to="/salud-trainingpeaks"
              class="card shadow-sm h-100 resumen-salud-card resumen-salud-card--link text-decoration-none text-reset"
              aria-label="Ver detalle de TrainingPeaks"
            >
              <div class="card-body py-3 d-flex flex-column">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <div class="resumen-salud-icon resumen-salud-icon--tp">
                    <i class="bi bi-calendar2-week"></i>
                  </div>
                  <span class="fw-semibold small">TrainingPeaks</span>
                </div>
                <template v-if="tpResumen?.configured">
                  <div v-for="entreno in (tpResumen.proximos || []).slice(0, 3)" :key="entreno.id || entreno.title" class="mb-1">
                    <div class="small fw-semibold text-truncate">{{ entreno.title }}</div>
                    <div class="text-muted" style="font-size:0.75rem;">
                      {{ entreno.etiqueta || formatDiaEtiqueta(entreno.date) }}
                      <span v-if="entreno.sport"> · {{ entreno.sport }}</span>
                    </div>
                  </div>
                  <div v-if="tpResumen.unread" class="small text-primary mt-1">
                    {{ tpResumen.unread }} comentario{{ tpResumen.unread === 1 ? '' : 's' }} nuevo{{ tpResumen.unread === 1 ? '' : 's' }}
                  </div>
                  <p v-if="!(tpResumen.proximos || []).length && !tpResumen.unread" class="text-muted small mb-0">
                    Sin plan próximo
                  </p>
                </template>
                <p v-else class="text-muted small mb-0">Sin conectar</p>
                <span class="btn btn-sm btn-outline-secondary mt-auto align-self-start resumen-salud-cta">
                  Ver detalle <i class="bi bi-arrow-right ms-1"></i>
                </span>
              </div>
            </router-link>
          </div>
        </div>
      </section>

      <hr class="my-4 resumen-admin-divider" />

      <Teleport to="#resumen-seccion-fire">
        <!-- FIRE -->
        <section class="resumen-admin mb-4">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div>
              <h4 class="mb-0">
                <i class="bi bi-fire me-2 text-danger"></i>FIRE
              </h4>
              <small class="text-muted">Metas y promedios desde Google Sheets</small>
            </div>
            <router-link to="/evolucion" class="btn btn-sm btn-outline-danger">
              Calculadora <i class="bi bi-arrow-right ms-1"></i>
            </router-link>
          </div>

          <div class="row g-3">
            <div v-if="metasFire.length" class="col-12 col-lg-8">
              <div class="card h-100">
                <div class="card-body py-3">
                  <h3 class="h6 mb-3">Metas FIRE</h3>
                  <div class="row g-2">
                    <div v-for="(meta, idx) in metasFire.slice(0, 4)" :key="idx" class="col-12 col-md-6">
                      <div class="resumen-meta-fire rounded p-3 h-100" :style="'border-left:4px solid ' + metaColor(idx)">
                        <div class="resumen-kpi-label text-uppercase">
                          <i class="bi bi-flag me-1"></i>{{ meta.etiqueta || 'Meta' }}
                        </div>
                        <div class="resumen-kpi-value fs-6">
                          {{ meta.importe }}
                          <span v-if="meta.descripcion" class="resumen-kpi-sub d-inline">{{ meta.descripcion }}</span>
                        </div>
                        <div v-if="meta.fechaAlcance || meta.faltan" class="d-flex flex-wrap align-items-center gap-2 mt-1">
                          <span v-if="meta.fechaAlcance" class="resumen-kpi-sub">
                            <i class="bi bi-calendar-event me-1"></i>{{ meta.fechaAlcance }}
                          </span>
                          <span v-if="meta.faltan" class="badge" :style="'background:' + metaColor(idx)">
                            Faltan {{ meta.faltan }}
                          </span>
                        </div>
                        <div
                          v-if="meta.diasRestantesTexto || meta.pctConseguidoTexto"
                          class="d-flex flex-wrap align-items-center gap-2 mt-2"
                        >
                          <span v-if="meta.diasRestantesTexto" class="resumen-kpi-sub">
                            <i class="bi bi-hourglass-split me-1"></i>{{ meta.diasRestantesTexto }}
                          </span>
                          <span
                            v-if="meta.pctConseguidoTexto"
                            class="badge bg-light text-dark border"
                            title="Porcentaje de la meta ya cubierto"
                          >
                            {{ meta.pctConseguidoTexto }} conseguido
                          </span>
                        </div>
                        <div v-if="meta.pctConseguido != null" class="progress mt-2" style="height:6px;">
                          <div
                            class="progress-bar"
                            role="progressbar"
                            :style="{ width: meta.pctBarWidth + '%', background: metaColor(idx) }"
                            :aria-valuenow="Math.round(meta.pctBarWidth)"
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12" :class="metasFire.length ? 'col-lg-4' : 'col-lg-12'">
              <div class="card h-100">
                <div class="card-body py-3">
                  <h3 class="h6 mb-3">Promedios diarios</h3>
                  <template v-if="promedios">
                    <div class="row g-2 text-center">
                      <div class="col-6">
                        <div class="resumen-kpi-label">Aportación</div>
                        <div class="resumen-kpi-value fs-6">{{ promedios.aportDiaria }}</div>
                      </div>
                      <div class="col-6">
                        <div class="resumen-kpi-label">Ganancia</div>
                        <div class="resumen-kpi-value fs-6 text-success">{{ promedios.ganDiaria }}</div>
                      </div>
                      <div class="col-6">
                        <div class="resumen-kpi-label">Total diario</div>
                        <div class="resumen-kpi-value fs-6 text-primary">{{ promedios.total }}</div>
                      </div>
                      <div class="col-6">
                        <div class="resumen-kpi-label">Gastos diarios</div>
                        <div class="resumen-kpi-value fs-6 text-danger">{{ promedios.gastosDiarios }}</div>
                      </div>
                    </div>
                    <div v-if="gastoMesRegistrado > 0" class="mt-3 pt-2 border-top small text-muted text-center">
                      Gasto mensual registrado (último año natural):
                      <strong class="text-dark">{{ formatEuros(gastoMesRegistrado) }} €/mes</strong>
                    </div>
                  </template>
                  <p v-else class="text-muted small mb-0">Sin datos de promedios.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Teleport>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from '../config/axios';
import {
  getMaxHistoricoFila,
  superaMaximoHistorico,
  pctVsMaximoHistorico,
  formatPctVsMaximo,
  parseMetasFire
} from '../utils/evolucionKpis';
import { formatMedicionAt, formatSyncAt, pickLatestSyncAt } from '../utils/formatSyncAt';

function parsePromedios(otros) {
  const headerIdx = (otros || []).findIndex(f => f.join(' ').toLowerCase().includes('aport'));
  if (headerIdx < 0) return null;

  const val = otros[headerIdx + 1] || [];
  const pct = otros[headerIdx + 2] || [];

  const euros = val.map((c) => ({ v: (c || '').trim() }))
    .filter(x => /[\d.,]+\s*€/.test(x.v));
  const pcts = pct.map((c) => ({ v: (c || '').trim() }))
    .filter(x => /%/.test(x.v));

  return {
    aportDiaria: euros[0]?.v || '',
    ganDiaria: euros[1]?.v || '',
    total: euros[2]?.v || '',
    gastosDiarios: euros[3]?.v || '',
    pctAport: pcts[0]?.v || '',
    pctGan: pcts[1]?.v || ''
  };
}

export default {
  name: 'ResumenAdminPaneles',
  setup() {
    const cargando = ref(true);
    const errorEvolucion = ref('');
    const pasado = ref([]);
    const otros = ref([]);
    const gastoMesRegistrado = ref(0);

    const menuActivo = ref(null);
    const ouraStatus = ref(null);
    const ouraLatest = ref(null);
    const fitdaysStatus = ref(null);
    const fitdaysLatest = ref(null);
    const corosStatus = ref(null);
    const proximoEntreno = ref(null);
    const corosTraining = ref(null);
    const tpResumen = ref(null);

    const hoy = computed(() => (pasado.value.length > 0 ? pasado.value[0] : null));
    const maxHistorico = computed(() => getMaxHistoricoFila(pasado.value));
    const esNuevoMaximoHistorico = computed(() =>
      superaMaximoHistorico(hoy.value, maxHistorico.value)
    );
    const pctVsMaximo = computed(() =>
      pctVsMaximoHistorico(hoy.value, maxHistorico.value)
    );
    const pctVsMaxLabel = computed(() => formatPctVsMaximo(pctVsMaximo.value));
    const metasFire = computed(() => parseMetasFire(otros.value));
    const promedios = computed(() => parsePromedios(otros.value));
    const menuHoy = computed(() =>
      (menuActivo.value?.dias || []).find((d) => d.fecha === menuActivo.value?.hoy) || null
    );
    const menuDiasCount = computed(() => (menuActivo.value?.dias || []).length);

    const ouraLastSync = computed(() =>
      pickLatestSyncAt(ouraStatus.value?.lastAutoSyncAt, ouraStatus.value?.lastSyncAt)
    );
    const ouraLastSyncLabel = computed(() =>
      ouraLastSync.value
        ? `Última sync: ${formatSyncAt(ouraLastSync.value, { compact: true })}`
        : 'Sin sincronizar'
    );
    const ouraLastSyncTitle = computed(() =>
      ouraLastSync.value
        ? `Última sincronización Oura: ${formatSyncAt(ouraLastSync.value)}`
        : 'Oura conectado, aún no hay sincronización registrada'
    );

    const fitdaysMedicionAt = computed(() => (
      fitdaysStatus.value?.ultimaMedicion?.measured_at
      || fitdaysLatest.value?.measured_at
      || fitdaysLatest.value?.measured_time
      || null
    ));
    const fitdaysMedicionLabel = computed(() => (
      fitdaysMedicionAt.value
        ? `Última medición: ${formatMedicionAt(fitdaysMedicionAt.value, { compact: true })}`
        : 'Sin mediciones'
    ));
    const fitdaysMedicionTitle = computed(() => (
      fitdaysMedicionAt.value
        ? `Última medición FitDays: ${formatMedicionAt(fitdaysMedicionAt.value)}`
        : 'FitDays configurado, aún no hay pesadas'
    ));

    const metaColor = (idx) => ['#6f42c1', '#0d6efd', '#198754', '#fd7e14'][idx % 4];

    const claseFirePct = (valor) => {
      if (!valor || valor === 'N/A') return 'text-muted';
      const num = parseFloat(String(valor).replace('%', '').replace(',', '.'));
      if (Number.isNaN(num)) return '';
      if (num >= 100) return 'text-success fw-bold';
      if (num >= 80) return 'text-success';
      if (num >= 60) return 'text-warning';
      return 'text-danger';
    };

    const formatEuros = (n) => {
      if (n == null || !Number.isFinite(n)) return '—';
      return new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(n);
    };

    const fmtScore = (v) => {
      if (v == null || v === '') return '—';
      return Math.round(Number(v));
    };

    const fmtPeso = (kg) => {
      if (kg == null) return '—';
      return Number(kg).toFixed(1);
    };

    const fmtDecimal = (v) => {
      if (v == null) return '—';
      return Number(v).toFixed(1);
    };

    const fmtEntrenoDia = (item) => {
      if (!item) return '—';
      if (item.day_label) return item.day_label;
      const s = String(item.happen_day || '');
      if (s.length === 8) return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
      return s || '—';
    };

    const formatDiaEtiqueta = (iso) => {
      if (!iso) return '';
      const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
    };

    async function cargarDatos() {
      cargando.value = true;
      errorEvolucion.value = '';

      const [evolRes, uanRes, nutRes, ouraStRes, fitStRes, corosStRes, tpRes] = await Promise.allSettled([
        axios.get('/api/evolucion/resumen'),
        axios.get('/api/gastos/resumen/ultimo-ano-natural'),
        axios.get('/api/nutricion/menu-activo'),
        axios.get('/api/oura/status'),
        axios.get('/api/fitdays/status'),
        axios.get('/api/coros/status'),
        axios.get('/api/trainingpeaks/resumen')
      ]);

      if (evolRes.status === 'fulfilled') {
        pasado.value = evolRes.value.data.pasado || [];
        otros.value = evolRes.value.data.otros || [];
      } else {
        errorEvolucion.value = evolRes.reason?.response?.data?.error || 'No se pudieron cargar los datos de evolución.';
      }

      if (uanRes.status === 'fulfilled') {
        const total = Number(uanRes.value.data?.totalNumero) || 0;
        gastoMesRegistrado.value = total > 0 ? total / 12 : 0;
      }

      if (nutRes.status === 'fulfilled') menuActivo.value = nutRes.value.data;

      if (ouraStRes.status === 'fulfilled') {
        ouraStatus.value = ouraStRes.value.data;
        if (ouraStatus.value?.connected) {
          try {
            const { data } = await axios.get('/api/oura/trends', { params: { days: 30, groupBy: 'day' } });
            ouraLatest.value = data.latest || null;
          } catch {
            ouraLatest.value = null;
          }
        }
      }

      if (fitStRes.status === 'fulfilled') {
        fitdaysStatus.value = fitStRes.value.data;
        if (fitdaysStatus.value?.configured) {
          try {
            const { data } = await axios.get('/api/fitdays/trends', { params: { days: 90, groupBy: 'day' } });
            fitdaysLatest.value = data.latest || null;
          } catch {
            fitdaysLatest.value = null;
          }
        }
      }

      if (corosStRes.status === 'fulfilled') {
        corosStatus.value = corosStRes.value.data;
        if (corosStatus.value?.configured) {
          try {
            const { data } = await axios.get('/api/coros/dashboard', { params: { days: 14 } });
            corosTraining.value = data.trainingStatus || null;
            proximoEntreno.value = data.hoyEntreno || null;
            if (!proximoEntreno.value) {
              const nw = await axios.get('/api/coros/next-workout');
              proximoEntreno.value = nw.data.workout || null;
            }
          } catch {
            corosTraining.value = null;
            try {
              const { data } = await axios.get('/api/coros/next-workout');
              proximoEntreno.value = data.workout || null;
            } catch {
              proximoEntreno.value = null;
            }
          }
        }
      }

      if (tpRes.status === 'fulfilled') {
        tpResumen.value = tpRes.value.data;
      }

      cargando.value = false;
    }

    onMounted(cargarDatos);

    return {
      cargando,
      errorEvolucion,
      hoy,
      maxHistorico,
      esNuevoMaximoHistorico,
      pctVsMaximo,
      pctVsMaxLabel,
      metasFire,
      promedios,
      gastoMesRegistrado,
      menuHoy,
      menuDiasCount,
      ouraStatus,
      ouraLatest,
      ouraLastSyncLabel,
      ouraLastSyncTitle,
      fitdaysStatus,
      fitdaysLatest,
      fitdaysMedicionLabel,
      fitdaysMedicionTitle,
      corosStatus,
      proximoEntreno,
      corosTraining,
      tpResumen,
      formatDiaEtiqueta,
      metaColor,
      claseFirePct,
      formatEuros,
      fmtScore,
      fmtPeso,
      fmtDecimal,
      fmtEntrenoDia
    };
  }
};
</script>

<style scoped>
.resumen-kpi-label {
  color: #6c757d;
  font-size: 0.78rem;
  margin-bottom: 0.25rem;
}

.resumen-kpi-value {
  color: #212529;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}

.resumen-kpi-sub {
  color: #6c757d;
  font-size: 0.78rem;
}

.resumen-meta-fire {
  background: #f8f9fa;
}

.resumen-salud-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.resumen-salud-icon--nutricion {
  background: rgba(255, 193, 7, 0.2);
  color: #b8860b;
}

.resumen-salud-icon--oura {
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
}

.resumen-salud-icon--fitdays {
  background: rgba(25, 135, 84, 0.12);
  color: #198754;
}

.resumen-salud-icon--coros {
  background: rgba(220, 53, 69, 0.12);
  color: #dc3545;
}

.resumen-salud-icon--tp {
  background: rgba(111, 66, 193, 0.12);
  color: #6f42c1;
}

.resumen-peso-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #198754;
  line-height: 1;
}

.resumen-salud-card {
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.resumen-salud-card--link {
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.resumen-salud-card--link:hover,
.resumen-salud-card--link:focus-visible {
  box-shadow: 0 0.35rem 0.85rem rgba(0, 0, 0, 0.12);
  border-color: rgba(13, 110, 253, 0.25);
}

.resumen-salud-cta {
  margin-top: 0.75rem;
  pointer-events: none;
}

@media (max-width: 575.98px) {
  .resumen-salud-cta {
    font-size: 0.75rem;
    padding: 0.2rem 0.45rem;
  }
}

.resumen-salud-sync {
  font-size: 0.7rem;
  margin-top: 0.5rem;
  line-height: 1.3;
}

.resumen-admin-divider {
  border-color: rgba(0, 0, 0, 0.08);
}

.evo-importe-total-valor {
  color: #212529;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}

.evo-importe-record {
  box-shadow: 0 0 0 1px rgba(255, 193, 7, 0.45), 0 0.25rem 0.75rem rgba(255, 193, 7, 0.2);
}

.evo-importe-record__valor {
  color: #b8860b !important;
}

.evo-pct-vs-max {
  font-size: 0.85rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.evo-pct-vs-max--up {
  color: #198754;
}

.evo-pct-vs-max--down {
  color: #dc3545;
}
</style>
